import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  RefreshCw,
  FlaskConical,
  Send,
  ShieldAlert,
  Activity,
  XCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ─── Types ────────────────────────────────────────────────

interface IntgEvento {
  id: string;
  source_system: string;
  event_type: string;
  direction: string;
  external_ref: string | null;
  status: string;
  is_sandbox: boolean;
  received_at: string;
  processed_at: string | null;
  hmac_valid: boolean | null;
  staging_count: number;
  conflitos: number;
  erros_abertos: number;
}

interface IntgErro {
  id: string;
  evento_id: string | null;
  mensagem: string;
  codigo_erro: string | null;
  tentativa: number;
  max_tentativas: number;
  proxima_tentativa: string | null;
  resolvido: boolean;
  is_sandbox: boolean;
  created_at: string;
}

interface AuditEntry {
  id: string;
  source_system: string;
  event_type: string;
  acao: string;
  tabela_alvo: string | null;
  diff_resumo: Record<string, unknown> | null;
  is_sandbox: boolean;
  created_at: string;
}

// ─── Status Badge ─────────────────────────────────────────

const statusConfig: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  pending:    { label: 'Pendente',    icon: Clock,         className: 'bg-muted/60 text-muted-foreground border-border/40' },
  processing: { label: 'Processando', icon: Loader2,       className: 'bg-info/10 text-info border-info/20' },
  done:       { label: 'Concluído',   icon: CheckCircle2,  className: 'bg-success/10 text-success border-success/20' },
  error:      { label: 'Erro',        icon: XCircle,       className: 'bg-destructive/10 text-destructive border-destructive/20' },
  skipped:    { label: 'Ignorado',    icon: AlertCircle,   className: 'bg-muted/60 text-muted-foreground border-border/40' },
  simulated:  { label: 'Simulado',    icon: FlaskConical,  className: 'bg-secondary/10 text-secondary border-secondary/20' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.pending;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`text-xs gap-1 ${cfg.className}`}>
      <Icon className="h-3 w-3" strokeWidth={1.5} />
      {cfg.label}
    </Badge>
  );
};

// ─── KPI Summary Cards ────────────────────────────────────

interface SummaryProps { eventos: IntgEvento[]; erros: IntgErro[] }

const SummaryCards: React.FC<SummaryProps> = ({ eventos, erros }) => {
  const total = eventos.length;
  const done = eventos.filter(e => e.status === 'done').length;
  const errored = eventos.filter(e => e.status === 'error').length;
  const conflicts = eventos.reduce((s, e) => s + (e.conflitos || 0), 0);
  const sandbox = eventos.filter(e => e.is_sandbox).length;
  const errosAbertos = erros.filter(e => !e.resolvido).length;

  const cards = [
    { label: 'Total de Eventos', value: total, icon: Activity, color: 'text-foreground', bg: 'bg-muted/40' },
    { label: 'Processados', value: done, icon: CheckCircle2, color: 'text-success', bg: 'bg-success/8' },
    { label: 'Conflitos', value: conflicts, icon: ShieldAlert, color: conflicts > 0 ? 'text-warning' : 'text-muted-foreground', bg: 'bg-warning/8' },
    { label: 'Erros Abertos', value: errosAbertos, icon: XCircle, color: errored > 0 ? 'text-destructive' : 'text-muted-foreground', bg: 'bg-destructive/8' },
    { label: 'Modo Sandbox', value: sandbox, icon: FlaskConical, color: 'text-secondary', bg: 'bg-secondary/8' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {cards.map(c => (
        <div key={c.label} className="emp-stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="emp-kpi-label">{c.label}</p>
              <p className={`text-2xl font-bold mt-1 ${c.color}`}>{c.value}</p>
            </div>
            <div className={`emp-icon-badge ${c.bg}`}>
              <c.icon className={`h-4 w-4 ${c.color}`} strokeWidth={1.5} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Event Row ────────────────────────────────────────────

const EventRow: React.FC<{ evento: IntgEvento }> = ({ evento }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr
        className="hover:bg-muted/20 transition-colors cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <td className="py-2.5 px-3">
          <div className="flex items-center gap-1.5">
            {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
            <span className="text-xs font-mono text-muted-foreground">{evento.id.slice(0, 8)}…</span>
          </div>
        </td>
        <td className="py-2.5 px-3">
          <span className="text-sm font-medium">{evento.source_system}</span>
        </td>
        <td className="py-2.5 px-3">
          <span className="text-xs text-muted-foreground font-mono">{evento.event_type}</span>
        </td>
        <td className="py-2.5 px-3 text-center">
          <StatusBadge status={evento.status} />
        </td>
        <td className="py-2.5 px-3 text-center">
          {evento.is_sandbox
            ? <Badge variant="outline" className="text-xs bg-secondary/10 text-secondary border-secondary/20">Sandbox</Badge>
            : <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/20">Produção</Badge>
          }
        </td>
        <td className="py-2.5 px-3 text-center">
          {evento.conflitos > 0
            ? <span className="text-warning font-semibold text-sm">{evento.conflitos}</span>
            : <span className="text-muted-foreground text-sm">—</span>}
        </td>
        <td className="py-2.5 px-3 text-right">
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(evento.received_at), { addSuffix: true, locale: ptBR })}
          </span>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="px-3 pb-3">
            <div className="bg-muted/30 rounded-lg p-3 text-xs font-mono text-muted-foreground space-y-1 border border-border/30">
              <p><span className="text-foreground font-semibold">external_ref:</span> {evento.external_ref || '—'}</p>
              <p><span className="text-foreground font-semibold">hmac_valid:</span> {evento.hmac_valid === null ? 'n/a' : String(evento.hmac_valid)}</p>
              <p><span className="text-foreground font-semibold">staging_count:</span> {evento.staging_count}</p>
              <p><span className="text-foreground font-semibold">processed_at:</span> {evento.processed_at ? new Date(evento.processed_at).toLocaleString('pt-BR') : '—'}</p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Sandbox Tester ───────────────────────────────────────

const defaultPayload = JSON.stringify({
  source_system: 'totvs',
  event_type: 'contract.updated',
  is_sandbox: true,
  external_ref: 'TOTVS-CONT-001',
  siafo_contrato_id: null,
  nome: 'Contrato Teste TOTVS',
  valor_global: 1500000.00,
  status: 'ativo',
  erp_id: 'ERP-001',
}, null, 2);

const SandboxTester: React.FC = () => {
  const [payload, setPayload] = useState(defaultPayload);
  const { toast } = useToast();
  const qc = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const parsed = JSON.parse(payload);
      const { data, error } = await supabase.functions.invoke('erp-webhook-receiver', {
        body: { ...parsed, is_sandbox: true },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({ title: 'Evento enviado com sucesso', description: `evento_id: ${data?.evento_id}` });
      qc.invalidateQueries({ queryKey: ['intg-monitor'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao enviar evento', description: err.message, variant: 'destructive' });
    },
  });

  return (
    <div className="space-y-3">
      <div className="emp-card p-4">
        <h3 className="emp-section-title mb-1">Payload JSON</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Forçado <code className="bg-muted px-1 rounded">is_sandbox: true</code> — nenhum dado operacional será alterado.
        </p>
        <Textarea
          value={payload}
          onChange={e => setPayload(e.target.value)}
          className="font-mono text-xs min-h-[220px] resize-y"
        />
        <div className="flex justify-end mt-3">
          <Button
            onClick={() => mutate()}
            disabled={isPending}
            className="gap-2 bg-emp-primary hover:bg-emp-primary/90"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Enviar Evento Sandbox
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────

const IntegracaoMonitorPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { role } = useAuth();
  const qc = useQueryClient();

  if (!['admin', 'coadmin'].includes(role || '')) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Acesso restrito a administradores.
      </div>
    );
  }

  const { data: eventos = [], isLoading: loadingEventos } = useQuery({
    queryKey: ['intg-monitor', 'eventos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vw_intg_monitor')
        .select('*')
        .limit(100);
      if (error) throw error;
      return data as IntgEvento[];
    },
    refetchInterval: 30000,
  });

  const { data: erros = [], isLoading: loadingErros } = useQuery({
    queryKey: ['intg-monitor', 'erros'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_intg_erros')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as IntgErro[];
    },
    refetchInterval: 30000,
  });

  const { data: auditLog = [], isLoading: loadingAudit } = useQuery({
    queryKey: ['intg-monitor', 'audit'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_intg_audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as AuditEntry[];
    },
    refetchInterval: 30000,
  });

  const isLoading = loadingEventos || loadingErros || loadingAudit;

  return (
    <div className="h-full flex flex-col gap-3 overflow-auto">
      {/* Header — hidden when embedded as tab in Configurações */}
      {!embedded && (
      <div className="flex items-center justify-between flex-wrap gap-3 flex-shrink-0">
        <div>
          <h2 className="text-base font-semibold text-foreground">Integração ERP</h2>
          <p className="text-[12px] text-muted-foreground mt-0.5">Sprint 4.1 — Fundação · Read-only + Sandbox</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-8"
          onClick={() => qc.invalidateQueries({ queryKey: ['intg-monitor'] })}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Atualizar
        </Button>
      </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-5 gap-3">{[...Array(5)].map((_, i) => <div key={i} className="emp-stat-card"><Skeleton className="h-14" /></div>)}</div>
          <div className="emp-card"><Skeleton className="h-48" /></div>
        </div>
      ) : (
        <>
          <SummaryCards eventos={eventos} erros={erros} />

          <Tabs defaultValue="eventos" className="flex-1 flex flex-col min-h-0">
            <TabsList className="h-8 self-start">
              <TabsTrigger value="eventos" className="text-xs h-7">
                Eventos {eventos.length > 0 && <span className="ml-1 text-xs bg-muted px-1.5 rounded-full">{eventos.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="erros" className="text-xs h-7">
                Erros {erros.filter(e => !e.resolvido).length > 0 && (
                  <span className="ml-1 text-xs bg-destructive/20 text-destructive px-1.5 rounded-full">{erros.filter(e => !e.resolvido).length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="audit" className="text-xs h-7">Auditoria</TabsTrigger>
              <TabsTrigger value="sandbox" className="text-xs h-7 gap-1">
                <FlaskConical className="h-3 w-3" />
                Sandbox
              </TabsTrigger>
            </TabsList>

            {/* Eventos */}
            <TabsContent value="eventos" className="flex-1 min-h-0 mt-3">
              <div className="emp-card p-4 overflow-auto" style={{ maxHeight: '60vh' }}>
                {eventos.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                    Nenhum evento registrado. Use o Sandbox para testar.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/40">
                        {['ID', 'Sistema', 'Tipo', 'Status', 'Ambiente', 'Conflitos', 'Recebido'].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/20">
                      {eventos.map(e => <EventRow key={e.id} evento={e} />)}
                    </tbody>
                  </table>
                )}
              </div>
            </TabsContent>

            {/* Erros */}
            <TabsContent value="erros" className="flex-1 min-h-0 mt-3">
              <div className="emp-card p-4 overflow-auto" style={{ maxHeight: '60vh' }}>
                {erros.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                    Nenhum erro registrado ✓
                  </div>
                ) : (
                  <div className="space-y-2">
                    {erros.map(err => (
                      <div key={err.id} className={`p-3 rounded-lg border text-sm ${err.resolvido ? 'border-border/30 opacity-50' : 'border-destructive/20 bg-destructive/5'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground">{err.mensagem}</p>
                            {err.codigo_erro && <p className="text-xs text-muted-foreground font-mono mt-0.5">{err.codigo_erro}</p>}
                          </div>
                          <div className="flex-shrink-0 flex items-center gap-2">
                            {err.is_sandbox && <Badge variant="outline" className="text-xs bg-secondary/10 text-secondary border-secondary/20">Sandbox</Badge>}
                            <Badge variant="outline" className={`text-xs ${err.resolvido ? 'bg-muted/60 text-muted-foreground' : 'bg-destructive/10 text-destructive border-destructive/20'}`}>
                              {err.resolvido ? 'Resolvido' : `Tentativa ${err.tentativa}/${err.max_tentativas}`}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1.5">
                          {formatDistanceToNow(new Date(err.created_at), { addSuffix: true, locale: ptBR })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Auditoria */}
            <TabsContent value="audit" className="flex-1 min-h-0 mt-3">
              <div className="emp-card p-4 overflow-auto" style={{ maxHeight: '60vh' }}>
                {auditLog.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                    Nenhuma entrada no log de auditoria.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/40">
                        {['Ação', 'Sistema', 'Evento', 'Tabela Alvo', 'Ambiente', 'Data'].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/20">
                      {auditLog.map(entry => (
                        <tr key={entry.id} className="hover:bg-muted/20">
                          <td className="py-2 px-3"><StatusBadge status={entry.acao} /></td>
                          <td className="py-2 px-3 text-sm">{entry.source_system}</td>
                          <td className="py-2 px-3 text-xs font-mono text-muted-foreground">{entry.event_type}</td>
                          <td className="py-2 px-3 text-xs text-muted-foreground">{entry.tabela_alvo || '—'}</td>
                          <td className="py-2 px-3">
                            {entry.is_sandbox
                              ? <Badge variant="outline" className="text-xs bg-secondary/10 text-secondary border-secondary/20">Sandbox</Badge>
                              : <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/20">Prod</Badge>
                            }
                          </td>
                          <td className="py-2 px-3 text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true, locale: ptBR })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </TabsContent>

            {/* Sandbox */}
            <TabsContent value="sandbox" className="flex-1 min-h-0 mt-3">
              <SandboxTester />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default IntegracaoMonitorPage;
